import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import apiDefinitions from '../../../../../core/api/apiDefinitions';
import { showError, showSuccess } from '../../../../../core/alert/sweetAlertConfig';

interface ItemDTO {
  id: number;
  itemName: string;
  brandId: number | null;
  description: string | null;
  capacity: string | null;
  serviceFrequency: string | null;
  itemTypeId: number | null;
  buildingId: number | null;
  floorId: number | null;
  roomId: number | null;
}

interface LocationDTO {
  id: number;
  name: string;
}

interface BuildingDTO {
  id: number;
  name: string;
}

interface FloorDTO {
  id: number;
  name: string;
}
interface UserDTO {
  id: number;
  name: string;
}

interface RoomDTO {
  id: number;
  name: string;
}

interface AddItemServiceCardModuleProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const AddItemServiceCardModule: React.FC<AddItemServiceCardModuleProps> = ({ show, onHide, onSuccess }) => {
  const [items, setItems] = useState<ItemDTO[]>([]);
  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [floors, setFloors] = useState<FloorDTO[]>([]);
  const [rooms, setRooms] = useState<RoomDTO[]>([]);
  const [users, setUsers] = useState<UserDTO[]>([]); // For storing users
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [scheduledEndDateTime, setScheduledEndDateTime] = useState<string | null>(''); // Optional
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch Locations
  const fetchLocations = async () => {
    setIsLoading(true);
    try {
      const payload = { filterType: '', filterValue: '', date: '' };
      const response = await apiDefinitions.getLocations('1', payload);
      if (response.status === 200) {
        setLocations(response.data.data.locations || []);
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch locations');
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || 'Error fetching locations');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Buildings
  const fetchBuildings = async () => {
    if (!selectedLocationId) return;
    setIsLoading(true);
    try {
      const response = await apiDefinitions.getBuildingsByLocationId(selectedLocationId);
      if (response.status === 200) {
        setBuildings(response.data.data || []);
        setSelectedBuildingId(null);
        setFloors([]);
        setSelectedFloorId(null);
        setRooms([]);
        setSelectedRoomId(null);
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch buildings');
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || 'Error fetching buildings');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Floors
  const fetchFloors = async () => {
    if (!selectedBuildingId) return;
    setIsLoading(true);
    try {
      const response = await apiDefinitions.getFloorsByBuildingId(selectedBuildingId);
      if (response.status === 200) {
        setFloors(response.data.data || []);
        setSelectedFloorId(null);
        setRooms([]);
        setSelectedRoomId(null);
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch floors');
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || 'Error fetching floors');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Rooms
  const fetchRooms = async () => {
    if (!selectedFloorId) return;
    setIsLoading(true);
    try {
      const response = await apiDefinitions.getRoomsByFloorId(selectedFloorId);
      if (response.status === 200) {
        setRooms(response.data.data || []);
        setSelectedRoomId(null);
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch rooms');
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || 'Error fetching rooms');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Items with Filters
  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const payload: any = {};
      if (selectedRoomId) {
        payload.filterType = 'roomId';
        payload.filterValue = String(selectedRoomId);
      } else if (selectedFloorId) {
        payload.filterType = 'floorId';
        payload.filterValue = String(selectedFloorId);
      } else if (selectedBuildingId) {
        payload.filterType = 'buildingId';
        payload.filterValue = String(selectedBuildingId);
      }
      const response = await apiDefinitions.getAllItems(1, payload);
      if (response.data.status === 'S1000') {
        setItems(response.data.data || []);
      } else {
        showError(response.data.statusDescription || 'Failed to fetch items');
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || 'Error fetching items');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Users (For selecting accountable user)
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await apiDefinitions.getUsers('1', {}); // Add the required filter
      if (response.status === 200) {
        setUsers(response.data.data.users || []);
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch users');
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || 'Error fetching users');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Form Submission
  const handleSubmit = async () => {
    if (!selectedItemId) {
      showError('Please select an item');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        itemId: Number(selectedItemId),
        scheduledDateTime,
        scheduledEndDateTime: scheduledEndDateTime || null, // Add scheduledEndDateTime (optional)
        accountableUserId: selectedUserId, // Add accountableUserId
        comment,
      };
      const response = await apiDefinitions.addItemServiceCard(payload);
      if (response?.data?.status === 'S1000') {
        showSuccess("Item service card added successfully");
        onSuccess();
        setSelectedItemId('');
        setScheduledDateTime('');
        setComment('');
        setSelectedLocationId(null);
        setSelectedBuildingId(null);
        setSelectedFloorId(null);
        setSelectedRoomId(null);
      } else {
        showError(response?.data?.statusDescription || 'Failed to add item service card');
      }
    } catch (error: any) {
      showError(error?.response?.data?.statusDescription || 'Error adding item service card');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load locations on mount
  useEffect(() => {
    fetchLocations();
  }, []);

  // Fetch buildings when location changes
  useEffect(() => {
    if (selectedLocationId) {
      fetchBuildings();
      fetchUsers();
    } else {
      setBuildings([]);
      setSelectedBuildingId(null);
      setFloors([]);
      setSelectedFloorId(null);
      setRooms([]);
      setSelectedRoomId(null);
      fetchUsers();
    }
  }, [selectedLocationId]);

  // Fetch floors when building changes
  useEffect(() => {
    if (selectedBuildingId) {
      fetchFloors();
    } else {
      setFloors([]);
      setSelectedFloorId(null);
      setRooms([]);
      setSelectedRoomId(null);
    }
  }, [selectedBuildingId]);

  // Fetch rooms when floor changes
  useEffect(() => {
    if (selectedFloorId) {
      fetchRooms();
    } else {
      setRooms([]);
      setSelectedRoomId(null);
    }
  }, [selectedFloorId]);

  // Fetch items when filters change
  useEffect(() => {
    fetchItems();
  }, [selectedLocationId, selectedBuildingId, selectedFloorId, selectedRoomId]);

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Item Service Card</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-3">
            {/* Left Column */}
            <Col md={6}>
              <Form.Label>Location</Form.Label>
              <Form.Select
                value={selectedLocationId || ''}
                onChange={(e) => setSelectedLocationId(Number(e.target.value) || null)}
                disabled={isLoading || locations.length === 0}
              >
                <option value="">Select a location</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </Form.Select>

              <Form.Label>Building</Form.Label>
              <Form.Select
                value={selectedBuildingId || ''}
                onChange={(e) => setSelectedBuildingId(Number(e.target.value) || null)}
                disabled={isLoading || buildings.length === 0}
              >
                <option value="">Select a building</option>
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Form.Select>

              <Form.Label>Floor</Form.Label>
              <Form.Select
                value={selectedFloorId || ''}
                onChange={(e) => setSelectedFloorId(Number(e.target.value) || null)}
                disabled={isLoading || floors.length === 0}
              >
                <option value="">Select a floor</option>
                {floors.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Right Column */}
            <Col md={6}>
              <Form.Label>Room</Form.Label>
              <Form.Select
                value={selectedRoomId || ''}
                onChange={(e) => setSelectedRoomId(Number(e.target.value) || null)}
                disabled={isLoading || rooms.length === 0}
              >
                <option value="">Select a room</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Form.Select>

              <Form.Label>Item</Form.Label>
              <Form.Select
                value={selectedItemId}
                onChange={(e) => setSelectedItemId(e.target.value)}
                required
                disabled={isLoading || items.length === 0}
              >
                <option value="">Select an item</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.itemName}
                  </option>
                ))}
              </Form.Select>

              <Form.Label>Scheduled DateTime</Form.Label>
              <Form.Control
                type="datetime-local"
                value={scheduledDateTime}
                onChange={(e) => setScheduledDateTime(e.target.value)}
                required
              />

              <Form.Label>Scheduled End DateTime</Form.Label>
              <Form.Control
                type="datetime-local"
                value={scheduledEndDateTime || ''}
                onChange={(e) => setScheduledEndDateTime(e.target.value)}
              />
            </Col>
          </Row>

          {/* Comment */}
          <Form.Group className="mb-3">
            <Form.Label>Comment</Form.Label>
            <Form.Control
              as="textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Form.Group>

          {/* Accountable User */}
          <Form.Group className="mb-3">
            <Form.Label>Accountable User</Form.Label>
            <Form.Select
              value={selectedUserId || ''}
              onChange={(e) => setSelectedUserId(Number(e.target.value) || null)}
              required
              disabled={isLoading || users.length === 0}
            >
              <option value="">Select a user</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide} disabled={isSubmitting || isLoading}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={isSubmitting || isLoading}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddItemServiceCardModule;
